<template>
  <div class="main">
    <div class="title-bar">
      <h1>Create an Account</h1>
    </div>
    <div class="container py-3 py-lg-5">
      <FormWrapper method="POST" :action="route('register')">
        <transition name="fade">
	        <div v-if="hasAlert">
		        <div class=" alert text-center alert-danger text-left" v-if="validationErrors.includes('hvacp_email')">
					<h2 class="mb-3">There was an issue creating your account.</h2>
					<p class="text-danger">This email address is associated with an HVACPartners account.  <br/>
					Please login with your HVACp account.</p>

					<a class="btn btn-info" :href="hvacpLoginUrl">HVACp Login</a>
		        </div>
	            <div class="text-center" v-else>
		            <Alert :alert="alert" />
	            </div>
	        </div>
        </transition>


        <div class="row">
          <div class="col-12 mb-3">
            <h3 class="text-primary">I am a...</h3>
          </div>
          <div class="col-12 mb-3">
            <Field
              class="d-inline mr-5"
              type="radio"
              id="user_type_contractor"
              name="user_type"
              label="HVAC Contractor employee"
              radio-value="contractor"
              v-model="userType"
            />
            <Field
              class="d-inline"
              type="radio"
              id="user_type_student"
              name="user_type"
              label="HVAC Student"
              radio-value="student"
              v-model="userType"
            />
          </div>

          <div class="col-12 mb-3">
            <hr />
            <h3 class="text-primary">Contact Information</h3>
          </div>
          <div class="col-6">
            <Field
              type="text"
              name="first_name"
              label="First Name"
              :value="user.first_name"
              validate="required"
              validate-as="First Name"
            />
          </div>
          <div class="col-6">
            <Field
              type="text"
              name="last_name"
              label="Last Name"
              :value="user.last_name"
              validate="required"
              validate-as="Last Name"
            />
          </div>
          <div class="col-6">
            <Field
              type="text"
              name="email"
              label="Email"
              :value="user.email"
              validate="required"
              validate-as="Email"
            />
          </div>
          <div class="col-3">
            <Field type="text" name="phone" label="Phone" :value="user.phone" />
          </div>
          <div class="col-3">
            <Field type="text" name="fax" label="Fax" :value="user.fax" />
          </div>

          <template v-if="userType == 'student'">
            <div class="col-12 mb-3">
              <hr />
              <h3 class="text-primary">Additional Information</h3>
            </div>
            <div class="col-6">
              <Field
                type="text"
                name="company[name]"
                label="Company or School Name"
                :value="user.company.name"
                validate="required"
                validate-as="Company/Organization"
              />
            </div>
            <div class="col-6"></div>
          </template>

          <template v-else>
            <div class="col-12 mb-3">
              <hr />
              <h3 class="text-primary">Company Information</h3>
            </div>
            <div class="col-6">
              <Field
                type="text"
                name="company[name]"
                label="Company Name"
                :value="user.company.name"
                validate="required"
                validate-as="Company Name"
              />
            </div>
            <div class="col-6">
              <Field
                type="text"
                name="title"
                label="Job Title"
                :value="user.title"
              />
            </div>
            <div class="col-6">
              <Field
                type="text"
                name="company[address1]"
                label="Address Line 1"
                :value="user.company.address1"
                validate="required"
                validate-as="Address Line 1"
              />
            </div>
            <div class="col-6">
              <Field
                type="text"
                name="company[address2]"
                label="Address Line 2"
                :value="user.company.address2"
              />
            </div>
            <div class="col-3">
              <Field
                type="text"
                name="company[city]"
                label="City"
                :value="user.company.city"
                validate="required"
                validate-as="City"
              />
            </div>
            <div class="col-3">
              <StateField
                name="company[state]"
                label="State/Province"
                :value="user.company.state"
              />
            </div>
            <div class="col-3">
              <Field
                type="text"
                name="company[zip]"
                label="Zip/Postal Code"
                :value="user.company.zip"
              />
            </div>
            <div class="col-3">
              <CountryField
                name="company[country]"
                label="Country"
                :value="user.company.country"
              />
            </div>
            <div class="col-3">
              <Field
                type="text"
                name="company[phone]"
                label="Company Phone"
                :value="user.company.phone"
              />
            </div>
            <div class="col-9"></div>
          </template>

          <div class="col-6">
            <Field
                type="text"
                name="hvac_distributor"
                label="HVAC Distributor"
                :value="user.hvac_distributor"
                :validate="userType !== 'student' ? 'required' : ''"
                validate-as="HVAC Distributor"
            />
          </div>
          <div class="col-6">
            <Field
                type="text"
                name="hvac_brand"
                label="HVAC Brand"
                :value="user.hvac_brand"
                :validate="userType !== 'student' ? 'required' : ''"
                validate-as="HVAC Brand"
            />
          </div>

          <div class="col-12 mb-3">
            <hr />
            <h3 class="text-primary">Additional branded access needed?</h3>
          </div>
          <div class="col-12 mb-3">
            <Field
                class="d-inline mr-5"
                type="radio"
                id="additional_access_yes"
                name="additional_access"
                label="Yes"
                :radio-value="1"
                v-model="additionalAccessNeeded"
            />
            <Field
                class="d-inline"
                type="radio"
                id="additional_access_no"
                name="additional_access"
                label="No"
                :radio-value="0"
                v-model="additionalAccessNeeded"
            />
          </div>
          <div class="col-12" v-if="additionalAccessNeeded > 0">
            <Field
                type="textarea"
                name="additional_access_reason"
                label="Explain why you need additional access:"
                :value="user.additional_access_reason"
            />
          </div>

          <div class="col-12 mb-3">
            <hr />
            <h3 class="text-primary">Login Credentials</h3>
          </div>
          <div class="col-6">
            <Field
              type="password"
              name="password"
              label="Password"
              value=""
              help-text="Minimum password length: 8 characters"
            />
          </div>
          <div class="col-6">
            <Field
              type="password"
              name="password_confirmation"
              label="Re-type Password"
              value=""
            />
          </div>

          <div class="col-12">
            <hr />
            <p>
              By checking the box below, you acknowledge that you have read and
              agree to the
              <a href="https://www.carrier.com/carrier/en/worldwide/legal/terms-of-use/"
                @click="openNewWindow('https://www.carrier.com/carrier/en/worldwide/legal/terms-of-use/')"
                class="text-primary">
                terms of use
              </a> and
              <a href="https://www.carrier.com/carrier/en/worldwide/legal/cookie-policy/"
                 @click="openNewWindow('https://www.carrier.com/carrier/en/worldwide/legal/cookie-policy/')"
                class="text-primary">
                privacy notice
              </a>.
            </p>
            <Field
              type="checkbox"
              id="agrees_to_terms"
              name="agrees_to_terms"
              :label="'Yes, I agree'"
              :value="false"
              :checkbox-value="1"
            />
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-12">
            <div class="buttons">
              <input
                type="submit"
                name="submit"
                value="Create Account"
                class="btn btn-primary mr-2"
              />
              <a :href="route('dashboard')" class="btn btn-secondary">Cancel</a>
            </div>
          </div>
        </div>
      </FormWrapper>
    </div>
  </div>
</template>

<script>
import DisplaysAlerts from "mixins/DisplaysAlerts";
export default {
  mixins: [DisplaysAlerts],
  props: {
    user: { type: Object },
    termsOfUse: { type: String, default: "" },
	  hvacpLoginUrl: { type: String, required: true },
  },
  data: () => {
    return {
      userType: "contractor",
      additionalAccessNeeded: 0,
    };
  },
  methods: {
    load() {
      this.userType = this.user.user_type;
      this.additionalAccessNeeded = this.user.additional_access;
    },
    openNewWindow(url) {
      event.preventDefault();
      window.open(url, "newwindow", "width=800,height=600");
      return false;
    }
  },
  mounted() {
    this.load();
  },
};
</script>
